import React, { ReactElement, useState } from 'react';
import cx from 'clsx';
import Box from '@material-ui/core/Box';
import Popover from '@material-ui/core/Popover';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Info from '@material-ui/icons/Info';
import Divider from '@material-ui/core/Divider';
import { useTranslation, Trans } from 'react-i18next';
import Button from '@material-ui/core/Button';

const useStyles = makeStyles((theme: Theme) => ({
  popoverWrap: {
    display: 'flex'
  },
  inline: {
    display: 'inline-flex'
  },
  centered: {
    alignItems: 'center'
  },
  description: {
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(1)
  },
  iconContainer: {
    lineHeight: 0
  },
  iconContainerMargin: {
    marginLeft: 8
  },
  icon: {
    color: theme.palette.secondary.main,
    fontSize: theme.typography.pxToRem(20),
    cursor: 'pointer'
  },
  popoverIconLabel: {
    marginRight: theme.spacing(1),
    color: theme.palette.secondary.main,
    fontSize: theme.typography.pxToRem(26)
  },
  popoverImage: {
    width: 300
  },
  paper: {
    marginTop: theme.spacing(1.5),
    borderRadius: 0,
    maxWidth: 300,
    overflow: 'visible',
    '&:before': {
      content: '""',
      width: 0,
      height: 0,
      left: 0,
      right: 0,
      top: theme.spacing(-1.5),
      position: 'absolute',
      margin: 'auto',
      borderLeft: '12px solid transparent',
      borderRight: '12px solid transparent',
      borderBottom: '12px solid #fff',
      filter: 'drop-shadow(-1px -1px 1px rgba(0,0,0,.2))'
    }
  },
  buttonLink: {
    '&:hover': {
      textDecoration: 'underline',
      cursor: 'pointer'
    }
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2)
  }
}));

interface PopoverProps {
  title?: string;
  image?: string;
  centerIcon?: boolean;
  inline?: boolean;
  description?: React.ReactNode;
  link?: string;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  onOpen?: () => void;
  onClose?: Function;
}

const PopoverWrap = ({
  children,
  icon,
  image,
  description,
  link,
  title,
  onOpen,
  onClose,
  centerIcon = true,
  inline
}: PopoverProps): ReactElement => {
  const classes = useStyles();
  const { t } = useTranslation('Tooltips');
  const [anchorEl, setAnchorEl] = useState<(EventTarget & HTMLSpanElement) | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLSpanElement>): void => {
    setAnchorEl(event.currentTarget);
    onOpen && onOpen();
  };

  const handlePopoverClose = (): void => {
    setAnchorEl(null);
    onClose && onClose();
  };

  const open = Boolean(anchorEl);

  return (
    <div
      className={cx(classes.popoverWrap, {
        [classes.centered]: centerIcon,
        [classes.inline]: inline
      })}
    >
      {children}
      <Box
        onClick={handlePopoverOpen}
        aria-owns={open ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        displayPrint="none"
        className={cx(classes.iconContainer, { [classes.iconContainerMargin]: !icon })}
      >
        {icon ?? <Info className={cx(classes.icon)} />}
      </Box>
      <Popover
        id="mouse-over-popover"
        classes={{
          paper: classes.paper
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
        onClose={handlePopoverClose}
        onMouseLeave={handlePopoverClose}
        disableRestoreFocus
      >
        {title && (
          <Typography className={classes.title} variant="h5">
            <Info className={classes.popoverIconLabel} /> {title}
          </Typography>
        )}
        {image && <img src={image} alt={title} className={classes.popoverImage} />}
        {description && (
          <Typography component="div" className={classes.description} variant="body1">
            <Trans>{description}</Trans>
          </Typography>
        )}
        <Divider />
        <div className={classes.footer}>
          {link && (
            <Button
              aria-label={t('Learn More')}
              variant="text"
              size="small"
              disableElevation
              href={link}
            >
              {t('Learn More')}
            </Button>
          )}
          <Button
            aria-label={t('Dismiss')}
            variant="contained"
            color="primary"
            size="small"
            disableElevation
            onClick={handlePopoverClose}
          >
            {t('Dismiss')}
          </Button>
        </div>
      </Popover>
    </div>
  );
};

export default PopoverWrap;
